import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { Widget, WidgetSetting, WidgetType } from '../classes';
import { serverLocation } from '../globals';
import { TokenService } from '../lib/shared/services/token.service';

@Injectable({
  providedIn: 'root'
})
export class WidgetService {

  widgetTypes: { [key: number]: ReplaySubject<WidgetType> } = {};
  allWidgetTypesLoaded = new BehaviorSubject(false);

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) { }

  createWidget(widget: Widget): Observable<Widget> {
    return this._httpClient.post(`${serverLocation}/api/widget`, widget, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Widget>;
  }

  createWidgetChartView(widgetId: number, file: string): Observable<WidgetSetting> {
    return this._httpClient.post(`${serverLocation}/api/widget-setting`, {
      widgetid: widgetId,
      widgetsettingdesc: 'custom-chart-view',
      widgetsettingvalue: file
    } as WidgetSetting, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<WidgetSetting>;
  }

  createWidgetIFrame(widgetId: number, href: string): Observable<WidgetSetting> {
    return this._httpClient.post(`${serverLocation}/api/widget-setting`, {
      widgetid: widgetId,
      widgetsettingdesc: 'iframe-href',
      widgetsettingvalue: href
    } as WidgetSetting, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<WidgetSetting>;
  }

  createWidgetSetting(widgetSetting: WidgetSetting): Observable<WidgetSetting> {
    return this._httpClient.post(`${serverLocation}/api/widget-setting`, widgetSetting, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<WidgetSetting>;
  }
  /**
   * the method deletes widget's by identifier
   * @param widgetId 
   * @returns 
   */
  deleteWidget(widgetId: number) {
    return this._httpClient.delete(`${serverLocation}/api/widget/${widgetId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  deleteWidgetSetting(identifier: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/widget-setting/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  deleteWidgetUserFilter(identifier: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/user-filter/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }
  /**
   * the method returns a widget type by identifier
   * @param identifier 
   * @param soft 
   * @returns 
   */
  getWidgetTypeById(identifier: number, soft: boolean = true) {
    if (!this.widgetTypes[identifier] || !soft) {
      if (!this.widgetTypes[identifier]) this.widgetTypes[identifier] = new ReplaySubject();
      this._getWidgetTypeById(identifier).subscribe({
        next: (value) => this.widgetTypes[identifier].next(value),
        error: (error) => this.widgetTypes[identifier].error(error)
      });
    }
    return this.widgetTypes[identifier];
  }
  /**
   * the method loads all widget types
   * @param soft
   * @returns
   */
  loadAllWidgetTypes(soft: boolean = true) {
    if (soft && this.allWidgetTypesLoaded.value) return combineLatest(Object.values(this.widgetTypes));
    let subject = new Subject();
    this.allWidgetTypesLoaded.next(false);
    this._httpClient.get(`${serverLocation}/api/widget-type`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).subscribe(
      (types: WidgetType[]) => {
        for (let type of types) {
          if (!this.widgetTypes[type.wtypeid]) this.widgetTypes[type.wtypeid] = new ReplaySubject();
          this.widgetTypes[type.wtypeid].next(type);
        }
        subject.next(types);
        this.allWidgetTypesLoaded.next(true);
      },
      (error: HttpErrorResponse) => subject.error(error),
      () => subject.complete()
    );
    return subject.asObservable();
  }

  updateWidget(widget: Widget): Observable<Widget> {
    return this._httpClient.put(`${serverLocation}/api/widget`, {
      widgetid: widget.widgetid,
      widgetuser: widget.widgetuser,
      widgetx: widget.widgetx,
      widgety: widget.widgety,
      widgeth: widget.widgeth,
      widgetw: widget.widgetw,
      widgettype: widget.widgettype
    } as Widget, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Widget>;
  }

  updateWidgetSetting(widgetSetting: WidgetSetting): Observable<WidgetSetting> {
    return this._httpClient.put(`${serverLocation}/api/widget-setting`, widgetSetting, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<WidgetSetting>;
  }
  /**
   * the method requests a widget type by identifier
   * @param identifier 
   * @returns 
   */
  private _getWidgetTypeById(identifier: number): Observable<WidgetType> {
    return this._httpClient.get(`${serverLocation}/api/widget-type/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<WidgetType>;
  }
}
